/**
 * WemwbsBarChart.tsx — Grouped bar chart (Pre vs Post per participant).
 *
 * Uses Victory (already in use across the dashboard — no new dependency).
 * VictoryGroup renders Pre and Post as two side-by-side bars per participant
 * so the improvement is visually obvious without a separate line.
 *
 * ANONYMITY: x-axis labels are participantRef ("P1"…"P8") — opaque,
 * non-identifying. No names, emails, or internal IDs are rendered.
 */

import React, { FC } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
    VictoryAxis,
    VictoryBar,
    VictoryChart,
    VictoryContainer,
    VictoryGroup,
    VictoryLegend,
    VictoryTheme,
    VictoryTooltip,
} from "victory";
import { WemwbsScores } from "./impact.mock";
import { verbalTheme } from "../../layout/themes/verbal.theme";

type WemwbsBarChartProps = {
    scores: WemwbsScores[];
};

export const WemwbsBarChart: FC<WemwbsBarChartProps> = ({ scores }) => {
    const theme = useTheme();

    const colorPre = theme.palette.info.main; // blue — "before"
    const colorPost = theme.palette.primary.main; // teal — "after" / improvement

    const preData = scores.map((s) => ({ x: s.participantRef, y: s.pre }));
    const postData = scores.map((s) => ({
        x: s.participantRef,
        y: s.post,
        label: `${s.participantRef}: +${s.delta} (${s.pre}→${s.post})`,
    }));

    const legend = [
        { name: "Pre-programme", symbol: { fill: colorPre, type: "square" as const } },
        { name: "Post-programme", symbol: { fill: colorPost, type: "square" as const } },
    ];

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                WEMWBS Scores — Pre vs Post
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
                Score range 14–70. Higher is better. Participant refs are anonymised labels.
            </Typography>
            <VictoryChart
                theme={VictoryTheme.material}
                containerComponent={<VictoryContainer responsive={true} />}
                width={620}
                height={260}
                domainPadding={{ x: 20 }}
                padding={{ top: 30, bottom: 40, left: 40, right: 20 }}
                domain={{ y: [0, 70] }}
            >
                <VictoryLegend
                    theme={VictoryTheme.material}
                    orientation="horizontal"
                    style={{ labels: { fontSize: 8 } }}
                    data={legend}
                    x={180}
                    y={4}
                />
                <VictoryAxis
                    theme={VictoryTheme.material}
                    style={{
                        tickLabels: { fontSize: 8, fontFamily: verbalTheme.typography.fontFamily },
                    }}
                />
                <VictoryAxis
                    dependentAxis
                    theme={VictoryTheme.material}
                    tickValues={[0, 14, 28, 42, 56, 70]}
                    tickFormat={(t: number) => String(t)}
                    style={{
                        tickLabels: { fontSize: 8, fontFamily: verbalTheme.typography.fontFamily },
                    }}
                />
                <VictoryGroup offset={10}>
                    <VictoryBar data={preData} style={{ data: { fill: colorPre } }} barWidth={8} />
                    <VictoryBar
                        data={postData}
                        style={{ data: { fill: colorPost } }}
                        barWidth={8}
                        labelComponent={
                            <VictoryTooltip
                                cornerRadius={() => 4}
                                flyoutPadding={6}
                                flyoutStyle={{ fill: "white", stroke: "#e3e3e3" }}
                                style={{ fontSize: 9, fontFamily: verbalTheme.typography.fontFamily }}
                                constrainToVisibleArea
                            />
                        }
                    />
                </VictoryGroup>
            </VictoryChart>
        </Box>
    );
};
